<template>
  <div class="outer-container">
    <div class="inner-container">
      <div class="wrapper">
        <div class="score-container">
          <div class="top">
            <div class="category-list">
              <button
                class="button"
                :class="{ active: activeButton === '0' }"
                @click="setActive('0')"
              >
                即时
              </button>
              <button
                class="button"
                :class="{ active: activeButton === '3' }"
                @click="setActive('3')"
              >
                赛果
              </button>
              <button
                class="button"
                :class="{ active: activeButton === '1' }"
                @click="setActive('1')"
              >
                赛程
              </button>
              <button
                class="button"
                :class="{ active: activeButton === '2' }"
                @click="setActive('2')"
              >
                收藏
              </button>
            </div>
            <ul class="actions_list">
              <li 
                class="action_item action_event_dropdown" 
                @click.stop="toggleEventDropdown"
              >
                <span>赛事</span>
                <img src="@assets/icons/score/more.webp" alt="下拉箭头" class="down-arrow" />
                
                <EventFilterDropdown
                  :visible="showEventDropdown"
                  :matches="plainMatches"
                  @update:visible="showEventDropdown = $event"
                  @confirm="handleEventConfirm"
                />
              </li>
              <li class="action_version_toggle">
                <VersionToggle
                  v-model="versionMode"
                  theme="basketball"
                  @change="handleVersionChange"
                />
              </li>

              <li class="action_item">
                <span>默认</span>
                <img src="@assets/icons/score/music.webp" alt="下拉箭头" class="down-arrow" />
              </li>
              <li 
                class="action_item action_settings_dropdown" 
                @click.stop="toggleSettingsDropdown"
                @mouseenter="handleSettingsMouseEnter"
                @mouseleave="showSettingsDropdown = false"
              >
                <span>设置</span>
                <img src="@assets/icons/score/setting.webp" alt="设置" class="settings-icon" />
                
                <SettingsDropdown
                  :visible="showSettingsDropdown"
                  @update:visible="showSettingsDropdown = $event"
                  @settings-change="handleSettingsChange"
                />
              </li>
            </ul>
          </div>
          <TabNavigation :menusList="menusList" @categorySelect="categorySelect" />
        </div>

        <!-- 专业版：左列表 + 右详情 -->
        <div class="list" v-if="versionMode === 'professional'">
          <div class="list-scroll" v-show="!loading && filteredMatches.length > 0">
            <MatchList :list="filteredMatches" @selectMatch="selectMatch" :hasReset="hasReset" />
          </div>
          <div class="match-detail" v-show="!loading && filteredMatches.length > 0">
            <MatchDetails :match="matchSelect.match" />
          </div>
          <SkeletonLoader :loading="loading" type="match-list" :count="6" v-if="loading" />
          <div v-if="!loading && filteredMatches.length <= 0" style="width: 100%; height: 100%">
            <div class="empty_list">
              <img src="@assets/icons/score/empty.webp" alt="Empty" class="empty-image" />
              <p>暂无数据</p>
            </div>
          </div>
        </div>

        <!-- 标准版：全宽单列表 -->
        <div class="list-standard" v-if="versionMode === 'standard'">
          <div v-show="!loading && filteredMatches.length > 0">
            <MatchListStandard :list="filteredMatches" :hasReset="hasReset" />
          </div>
          <SkeletonLoader :loading="loading" type="match-list" :count="6" v-if="loading" />
          <div v-if="!loading && filteredMatches.length <= 0" style="width: 100%; height: 100%">
            <div class="empty_list">
              <img src="@assets/icons/score/empty.webp" alt="Empty" class="empty-image" />
              <p>暂无数据</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import TabNavigation from './TabNavigation.vue'
import MatchList from './MatchList.vue'
import MatchListStandard from './MatchListStandard.vue'
import MatchDetails from './MatchDetails.vue'
import VersionToggle from '@/components/common/VersionToggle.vue'
import SettingsDropdown from './SettingsDropdown.vue'
import EventFilterDropdown from '@/components/EventFilterDropdown.vue'
import pako from 'pako'
import { ref, onUnmounted, onMounted, reactive, watch, computed } from 'vue'
import { getMatchBasketballList } from '@/api/match'
import { getBasketballMatchCategory } from '@/api/match'
// import ScoreBasketballRunning from './ScoreBasketballRunning.vue'
// import ScoreBasketballNotStart from './ScoreBasketballNotStart.vue'
// import ScoreBasketballEnd from './ScoreBasketballEnd.vue'
import { fromJS, Map } from 'immutable'
import { getDQToken } from '@/api/user'
import { message } from 'ant-design-vue'
import { decompressData } from '@/utils/dataProcessing'
import SkeletonLoader from '@/components/SkeletonLoader.vue'

let ws = null
const list = ref(fromJS([]))
const plainMatches = computed(() => list.value.toJS())

// 过滤后的比赛列表(根据选中的赛事筛选)
const filteredMatches = computed(() => {
  // 如果没有选中任何赛事,显示所有比赛
  if (selectedEventNames.value.length === 0) {
    return plainMatches.value
  }
  
  // 根据选中的赛事名称过滤比赛
  return plainMatches.value.filter(match => {
    const leagueName = match.league_name || match.title
    return selectedEventNames.value.includes(leagueName)
  })
})

const myProps = defineProps(['categoryId', 'status'])
const categoryIdRef = ref(null)
const stateRef = ref(null)
let pollingInterval = null
const menusList = ref([])
const activeButton = ref('0')
const init = ref(false)
const loading = ref(true)

const hasReset = ref(false)
const matchSelect = reactive({
  match: {}
})

// 新增：版本模式状态
const versionMode = ref('professional')

// 新增：设置下拉框状态
const showSettingsDropdown = ref(false)

// 新增：赛事下拉框状态
const showEventDropdown = ref(false)

// 新增：选中的赛事名称列表(用于前端过滤)
const selectedEventNames = ref([])

const getBasketballCetegoryLists = () => {
  getBasketballMatchCategory()
    .then((res) => {
      console.log(res)
      menusList.value = res.data
    })
    .catch((error) => {
      console.log(error)
    })
}
const setActive = (button) => {
  // activeButton.value = button
  if (button === '2') {
    message.success('当前无收藏的比赛')
    return
  }
  activeButton.value = button
  stateRef.value = parseInt(button)
  getLists()
}
const selectMatch = (res) => {
  matchSelect.match = res
}

const updateMatches = (newData) => {
  const newImmutableData = fromJS(newData)

  if (newImmutableData) {
    // console.log(newImmutableData, 'newImmutableData')
    list.value = list.value.map((match) => {
      let updatedMatch = newImmutableData.find(
        (newMatch) => newMatch.get('match_id') === parseInt(match.get('match_id'))
      )
      if (updatedMatch?.toObject()) {
        // console.log(updatedMatch?.toObject(), 'updatedMatch')
        // console.log(match.merge(updatedMatch).toJS(), 'match data')
      }

      return updatedMatch?.toObject() ? match.merge(updatedMatch) : match
    })
    // console.log(list.value.toJS(), 'list.value')
  }
}

const categorySelect = (tab) => {
  console.log(tab, 'tab')
  categoryIdRef.value = tab.leagueIds
  getLists()
}
const getMatchBasketballLists = () => {
  getMatchBasketballList({
    leagueId: categoryIdRef.value ? categoryIdRef.value : null,
    state: stateRef.value ? stateRef.value : null
  })
    .then((res) => {
      if (res.code === 200) {
        const decompressedData = decompressData(res.data)
        if (decompressedData) {
          list.value = fromJS(decompressedData)
          if (!init.value) {
            matchSelect.match = list.value.toJS()[0]
            init.value = true
          }
        } else {
          list.value = fromJS([])
        }
      } else {
        console.error(`获取比赛列表失败: ${res.msg}`)
        list.value = fromJS([])
      }
    })
    .catch((error) => {
      console.error(`获取比赛列表失败: ${error}`)
      list.value = fromJS([])
    })
    .finally(() => {
      console.log('finally')
      loading.value = false
    })
}
const getLists = () => {
  loading.value = true
  list.value = fromJS([])
  getMatchBasketballList({
    leagueId: categoryIdRef.value ? categoryIdRef.value : null,
    state: stateRef.value ? stateRef.value : null
  })
    .then((res) => {
      if (res.code === 200) {
        const decompressedData = decompressData(res.data)
        if (decompressedData) {
          list.value = fromJS(decompressedData)
          hasReset.value = true
          setTimeout(() => {
            hasReset.value = false
          }, 100)
        } else {
          list.value = fromJS([])
        }
      } else {
        console.error(`获取比赛列表失败: ${res.msg}`)
        list.value = fromJS([])
      }
    })
    .catch((error) => {
      console.error(`获取比赛列表失败: ${error}`)
      list.value = fromJS([])
    })
    .finally(() => {
      console.log('finally')
      loading.value = false
    })
}
const processData = (data) => {
  try {
    // 分割并提取消息数据
    const message = data.split('42/dqiu-prod,')[1]
    if (!message) {
      console.error('Invalid message format')
      return
    }

    // 去掉 "10" 之前的部分
    const cleanedMessage = message.replace(/^\d+/, '')

    const parsedMessage = JSON.parse(cleanedMessage)

    // 检查数据的结构
    if (!Array.isArray(parsedMessage) || parsedMessage.length < 2) {
      console.error('Unexpected parsed message structure')
      return
    }

    const eventType = parsedMessage[0]
    const eventData = parsedMessage[1]

    if (eventType !== 'message' || !eventData || !eventData.msg) {
      console.error('Unexpected event type or missing event data')
      return
    }

    const { msg } = eventData

    if (!Array.isArray(msg) || msg.length === 0) {
      console.error('No message data found')
      return
    }

    const extractedData = msg.reduce((acc, match) => {
      if (match.sportId === 2) {
        acc[match.matchId] = {
          matchId: match.matchId,
          match_id: match.matchId,
          sportId: match.sportId,
          createTime: match.createTime,
          messageId: match.messageId,
          startTime: match.startTime,
          matchTime: match.startTime,
          homeTeamName: match.homeTeamName,
          awayTeamName: match.awayTeamName,
          timePlayed: match.timePlayed,
          timeRemaining: match.timeRemaining,
          timeRunning: match.timeRunning,
          timeUpdate: match.timeUpdate,
          updateTimestamp: match.updateTimestamp,
          status: match.status,
          home_grades: match.homeTeamScore,
          away_grades: match.awayTeamScore,
          home_total_score: match.homeTeamScore,
          guest_total_score: match.awayTeamScore,
          match_status: match.status,
          match_status_code: match.statusCode,
          count_down: match.timeRemaining,
          status_code: match.statusCode,
          statusCode: match.statusCode,
          awayTeamScore: match.awayTeamScore,
          awayTeamHalfTimeScore: match.awayTeamHalfTimeScore,
          awayTeamNormalTimeScore: match.awayTeamNormalTimeScore,
          homeTeamScore: match.homeTeamScore,
          homeTeamHalfTimeScore: match.homeTeamHalfTimeScore,
          homeTeamNormalTimeScore: match.homeTeamNormalTimeScore,
          awayTeamPenaltyScore: match.awayTeamPenaltyScore,
          awayTeamRed: match.awayTeamRed,
          awayTeamYellow: match.awayTeamYellow,
          awayTeamCorner: match.awayTeamCorner,
          homeTeamPenaltyScore: match.homeTeamPenaltyScore,
          homeTeamRed: match.homeTeamRed,
          homeTeamYellow: match.homeTeamYellow,
          homeTeamCorner: match.homeTeamCorner,
          homeTeamBallPossession: match.homeTeamBallPossession,
          awayTeamBallPossession: match.awayTeamBallPossession,
          homeTeamAttacks: match.homeTeamAttacks,
          awayTeamAttacks: match.awayTeamAttacks,
          homeTeamGoalKicks: match.homeTeamGoalKicks,
          awayTeamGoalKicks: match.awayTeamGoalKicks,
          homeTeamShotsOnGoal: match.homeTeamShotsOnGoal,
          awayTeamShotsOnGoal: match.awayTeamShotsOnGoal,
          homeTeamTimeouts: match.homeTeamTimeouts,
          awayTeamTimeouts: match.awayTeamTimeouts,
          homeTeamFouls: match.homeTeamFouls,
          awayTeamFouls: match.awayTeamFouls,
          homeTeamFieldGoalAttempted: match.homeTeamFieldGoalAttempted,
          awayTeamFieldGoalAttempted: match.awayTeamFieldGoalAttempted,
          homeTeamFieldGoalMade: match.homeTeamFieldGoalMade,
          awayTeamFieldGoalMade: match.awayTeamFieldGoalMade,
          homeTeamThreePointAttempted: match.homeTeamThreePointAttempted,
          awayTeamThreePointAttempted: match.awayTeamThreePointAttempted,
          homeTeamThreePointMade: match.homeTeamThreePointMade,
          awayTeamThreePointMade: match.awayTeamThreePointMade,
          homeTeamFreeThrowAttempted: match.homeTeamFreeThrowAttempted,
          awayTeamFreeThrowAttempted: match.awayTeamFreeThrowAttempted,
          homeTeamFreeThrowMade: match.homeTeamFreeThrowMade,
          awayTeamFreeThrowMade: match.awayTeamFreeThrowMade,
          homeTeamPeriod1Score: match.homeTeamPeriod1Score,
          homeTeamPeriod2Score: match.homeTeamPeriod2Score,
          homeTeamPeriod3Score: match.homeTeamPeriod3Score,
          homeTeamPeriod4Score: match.homeTeamPeriod4Score,
          awayTeamPeriod1Score: match.awayTeamPeriod1Score,
          awayTeamPeriod2Score: match.awayTeamPeriod2Score,
          awayTeamPeriod3Score: match.awayTeamPeriod3Score,
          awayTeamPeriod4Score: match.awayTeamPeriod4Score,
          home_section_score: [
            match.homeTeamPeriod1Score,
            match.homeTeamPeriod2Score,
            match.homeTeamPeriod3Score,
            match.homeTeamPeriod4Score
          ],
          guest_section_score: [
            match.awayTeamPeriod1Score,
            match.awayTeamPeriod2Score,
            match.awayTeamPeriod3Score,
            match.awayTeamPeriod4Score
          ],
          homeTeamRemainingPause: match.homeTeamRemainingPause,
          awayTeamRemainingPause: match.awayTeamRemainingPause,
          homeTeamAssist: match.homeTeamAssist,
          awayTeamAssist: match.awayTeamAssist,
          homeTeamTurnover: match.homeTeamTurnover,
          awayTeamTurnover: match.awayTeamTurnover,
          homeTeamRebound: match.homeTeamRebound,
          awayTeamRebound: match.awayTeamRebound,
          homeTeamGameScore: match.homeTeamGameScore,
          awayTeamGameScore: match.awayTeamGameScore,
          homeTeamOvertimeScore: match.homeTeamOvertimeScore,
          awayTeamOvertimeScore: match.awayTeamOvertimeScore,
          homeTeamLastPeriodScore: match.homeTeamLastPeriodScore,
          awayTeamLastPeriodScore: match.awayTeamLastPeriodScore,
          side: match.side,
          latencyOfSource: match.latencyOfSource
        }
      }
      // console.log(acc, 'acc')
      return acc
    }, {})

    return extractedData
  } catch (error) {
    console.error('Error processing data:', error)
  }
}

const initSocket = async () => {
  // let userId = generateUserID()
  // let unix = dayjs().valueOf()
  try {
    let tokenData = await getDQToken()
    console.log(tokenData, 'token')
    const token = tokenData.data.token
    const wss = tokenData.data.connectionWsAddress
    ws = new WebSocket(
      `${wss}/socket.io/?token=${encodeURIComponent(token)}&EIO=3&transport=websocket`
    )

    let heartbeatInterval

    ws.onopen = function () {
      console.log('WebSocket connected!')
      let sendmsg1 = `40/dqiu-prod?token=${token}`
      ws.send(sendmsg1)
      let msg2 = `42/dqiu-prod,["join","livescore/*"]`
      ws.send(msg2)

      // Start heartbeat
      startHeartbeat()
    }

    ws.onmessage = function (event) {
      // console.log('Message from server: ' + event.data)
      let newData = processData(event.data)
      updateMatches(newData)
      if (event.data === '1') {
        ws.send('2')
      } else {
        let msg = { appId: 'dqiu-prod', msgId: 'AZCmQfMtvlEPnuKUQHdh', room: '/livescore/10400496' }
      }
      // Reset heartbeat on any message from server
      // resetHeartbeat()
    }

    ws.onclose = function () {
      console.log('WebSocket closed!')
      // Clear the heartbeat interval
      clearInterval(heartbeatInterval)
    }

    ws.onerror = function (error) {
      console.log('WebSocket error: ' + error.type)
    }

    const startHeartbeat = () => {
      heartbeatInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('2')
          console.log('Heartbeat sent: ping')
        }
      }, 10000) // Send a ping every 30 seconds
    }

    const resetHeartbeat = () => {
      clearInterval(heartbeatInterval)
      startHeartbeat()
    }
  } catch (error) {
    console.log(error, 'error')
  }
}

const startPolling = () => {
  // 清除任何现有的轮询
  if (pollingInterval) {
    clearInterval(pollingInterval)
  }
  // 设置新的轮询，每10秒调用一次
  pollingInterval = setInterval(() => {
    getMatchBasketballLists()
  }, 20000)
}

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
  }
}

const closeSocket = () => {
  if (ws) {
    ws.close()
    ws = null
  }
}

// 新增：处理版本模式切换
const handleVersionChange = (version) => {
  versionMode.value = version
  console.log('切换版本模式:', version)
  // TODO: 根据版本模式切换不同的数据展示或布局
  // 可以在这里实现：
  // - 标准版：显示基础数据和简化布局
  // - 专业版：显示详细数据和完整功能
}

// 新增：切换设置下拉框
const toggleSettingsDropdown = () => {
  showSettingsDropdown.value = !showSettingsDropdown.value
}

// 新增：处理鼠标悬停
const handleSettingsMouseEnter = () => {
  showSettingsDropdown.value = true
}

// 新增：处理设置变更
const handleSettingsChange = (newSettings) => {
  console.log('设置已更新:', newSettings)
  // TODO: 后续可以根据设置调整页面行为
  // - 根据语言切换界面语言
  // - 根据显示排名控制排名显示
  // - 根据排序规则调整比赛列表排序
  // - 根据提示范围控制通知范围
}

// 新增：切换赛事下拉框
const toggleEventDropdown = () => {
  showEventDropdown.value = !showEventDropdown.value
}

// 新增：处理赛事筛选确认
const handleEventConfirm = (selectedEvents) => {
  console.log('选中的赛事:', selectedEvents)
  
  // 保存选中的赛事名称，用于前端过滤
  selectedEventNames.value = selectedEvents.names
  
  // 如果有选中的赛事ID，可以调用API进行后端过滤
  // categoryIdRef.value = selectedEvents.ids.join(',')
  // getLists()
  
  // 关闭下拉框
  showEventDropdown.value = false
}
onMounted(() => {
  getMatchBasketballLists() // 立即获取一次数据
  startPolling() // 开始轮询
  getBasketballCetegoryLists()
  // initSocket()
})

onUnmounted(() => {
  stopPolling() // 停止轮询
  // closeSocket()
})
</script>

<style scoped lang="scss">
.wrapper {
  position: relative;
  z-index: 3;
}
.score-container {
  position: sticky;
  top: 70px;
  z-index: 10;

  background: $bg-page;
  .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .category-list {
    display: flex;
    padding-bottom: 10px;
    padding-top: 10px;
  }
  .actions_list {
    display: flex;
    align-items: center;
    list-style: none;
    justify-content: flex-end;
    padding: 0;
    margin: 0;
    margin-right: 0;
    .action_item {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family:
        PingFang SC,
        PingFang SC;
      font-weight: 500;
      font-size: 12px;
      color: $text-secondary;
      width: 72px;
      height: 30px;
      background: $bg-white;
      border-radius: 20px 20px 20px 20px;
      border: 1px solid $border-color;
      margin-right: 10px;
      transition: all 0.3s ease;
      
      span {
        margin-right: 4px;
        transition: color 0.3s ease;
      }

      &:hover {
        background: $bg-white;
        span {
          color: $color-primary;
        }
      }
    }
    // 新增：版本切换按钮容器样式
    .action_version_toggle {
      display: flex;
      align-items: center;
      margin-right: 10px;
    }

    // 新增：设置下拉菜单样式
    .action_settings_dropdown {
      position: relative;

      &:hover {
        background: $bg-white;
        span {
          color: $color-primary;
        }
      }
    }

    // 新增：赛事下拉菜单样式
    .action_event_dropdown {
      position: relative;

      &:hover {
        background: $bg-white;
        span {
          color: $color-primary;
        }
      }
    }

    .down-arrow,
    .settings-icon {
      width: 16px;
      height: 16px;
    }
  }
}

.button {
  width: 64px; /* 根据需要调整按钮大小 */
  height: 36px; /* 根据需要调整按钮大小 */
  border: 1px solid $border-color;
  border-radius: 999px;
  cursor: pointer;
  background: $bg-white;
  margin-right: 20px;
  font-weight: 500;
  font-size: 14px;
  color: $text-dark;
  box-shadow: none;
}

.button.active {
  background: $color-primary;
  border-color: transparent;
  box-shadow: 0px 10px 20px 0px rgba($color-primary, 0.25);
  color: $text-white;
  /* 在需要时添加额外的选中样式 */
}

.outer-container {
  width: 100%;
  background: $bg-page;
  box-sizing: border-box;
  min-height: 100vh;
}

.inner-container {
  width: 1200px;
  box-sizing: border-box;
  margin: 0 auto;

  background: $bg-page;
}

/* 最小宽度限制 */
@media (max-width: 1400px) {
  .inner-container {
    min-width: 1200px;
  }
}
.list {
  display: flex;
  justify-content: space-between;
  position: relative;
}
.list-scroll {
  height: 100%;
  padding-right: 10px;
  flex: 1;
}
.empty_list {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  height: 70vh;
  background: $bg-white;
  color: $text-secondary;
  .empty-image {
    width: 300px;
  }
}
.match-detail {
  // position: sticky;
  // top: 200px;
  width: 375px;
  // margin-left: 10px;
  // background: $bg-white;
  border-radius: 10px;
  // height: 750px;
  // overflow: hidden;
  z-index: 1;
}
.match-skeleton {
  background: $bg-white;
}

// 标准版样式
.list-standard {
  width: 100%;
  
  .empty_list {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    height: 70vh;
    background: $bg-white;
    color: $text-secondary;
    
    .empty-image {
      width: 300px;
    }
  }
}
</style>
